import type { FetchPolicy } from '@apollo/client';
import { LoadingMessage } from '@communities/community/components';
import { useCommunityDetail } from '@communities/community/hooks';
import { SearchPanel, StickySearchPanel } from '@content-search/components';
import { SearchResults } from '@content-search/components/SearchResults';
import type { PaginationConfig } from '@content-search/domain';
import { useContentSearch, useSearchState, useSearchScrollPosition } from '@content-search/hooks';
import { useStandardLabels } from '@content-search/hooks/useStandardLabels';
import { buildSearchInput } from '@content-search/utils';
import { ErrorBanner, AudioPlayerProvider } from '@core/components';
import { HeroBackground } from '@core/components/HeroBackground';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { useCallback, useEffect, useState } from 'react';

import * as styles from './ContentSearchPage.css';

function ContentSearchPageComponent() {
  const { standardLabels } = useStandardLabels();
  const { community, loading } = useCommunityDetail();

  const { isTargetOutOfView } = useSearchScrollPosition({
    targetSelector: '[data-search-filters-container]',
  });

  const { searchResult, isSearching, errors, search } = useContentSearch();
  const { searchFilters, pagination, hasSearched, updateSearchState, setHasSearched } = useSearchState();

  // Track conversation context state
  const [showingConversationContext, setShowingConversationContext] = useState(false);

  // Search handler that forces a fresh search when explicitly triggered
  const handleSearch = useCallback(
    (filtersToUse = searchFilters, userId?: string, subjectId?: string) => {
      if (!community?.id || standardLabels.length === 0) return;

      // Create final filters with any overrides
      const finalFilters = {
        ...filtersToUse,
        userId: userId !== undefined ? userId : filtersToUse.userId,
        subjectId: subjectId !== undefined ? subjectId : filtersToUse.subjectId,
      };

      // Build search input and execute search immediately with cache-and-network to force fresh data
      const searchInput = buildSearchInput({
        communityId: community.id,
        filters: finalFilters,
        paginationConfig: { currentPage: 1, pageSize: pagination.pageSize },
        standardLabels,
      });

      // Force fresh search by using cache-and-network fetch policy
      search(searchInput, 'cache-and-network' as FetchPolicy);

      // Update URL state after executing search
      updateSearchState({
        filters: finalFilters,
        pagination: { currentPage: 1 }, // Reset to first page on new search
      });
      setHasSearched(true);
    },
    [community, searchFilters, updateSearchState, setHasSearched, standardLabels, search, pagination.pageSize]
  );

  // Simplified pagination handler - just updates URL, search execution happens automatically
  const handlePaginationChange = useCallback(
    (newPagination: PaginationConfig) => {
      if (!community?.id) return;

      updateSearchState({ pagination: newPagination });

      // Mark as searched if this is the first interaction
      if (!hasSearched) {
        setHasSearched(true);
      }
    },
    [community, updateSearchState, hasSearched, setHasSearched]
  );

  // Auto-execute search whenever URL state changes (filters or pagination)
  useEffect(() => {
    if (hasSearched && community?.id && standardLabels.length > 0) {
      const searchInput = buildSearchInput({
        communityId: community.id,
        filters: searchFilters,
        paginationConfig: pagination,
        standardLabels,
      });

      search(searchInput);
    }
  }, [hasSearched, community, searchFilters, pagination, standardLabels, search]);

  if (loading) {
    return <LoadingMessage />;
  }

  return (
    <AudioPlayerProvider>
      <HeroBackground imageUrl={community?.superHeroArtUrl} />
      <div>
        {/* Header Section */}
        <div className={styles.headerSection}>
          {/* Hero Overlay for smooth gradient transition */}
          <div className={styles.heroOverlay} />

          {/* Sticky Search Panel - Hidden when in conversation context */}
          {!showingConversationContext && (
            <StickySearchPanel
              isLoading={isSearching}
              isVisible={isTargetOutOfView}
              searchFilters={searchFilters}
              setSearchFilters={filters => updateSearchState({ filters })}
              onSearch={handleSearch}
            />
          )}

          {/* Page Header */}
          <h1 className={styles.pageHeader}>Search {community?.name || 'Community'} Content</h1>

          {/* Search Panel */}
          <div data-search-filters-container className={styles.searchFiltersContainer}>
            <SearchPanel
              isLoading={isSearching}
              searchFilters={searchFilters}
              setSearchFilters={filters => updateSearchState({ filters })}
              onSearch={handleSearch}
            />
          </div>
        </div>

        {/* Content Section with Solid Background */}
        <div className={styles.contentSection}>
          <div className={styles.contentContainer}>
            {/* Error Banner */}
            {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error searching for content:" />}

            {/* Search Results */}
            <SearchResults
              communityId={community?.id}
              hasSearched={hasSearched}
              isLoading={isSearching}
              pagination={pagination}
              searchResults={searchResult}
              setPagination={handlePaginationChange}
              onContextViewChange={setShowingConversationContext}
            />
          </div>
        </div>
      </div>
    </AudioPlayerProvider>
  );
}

export const ContentSearchPage = withAppInsightsTracking(ContentSearchPageComponent, 'ContentSearchPage');
